import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { User, UserSession } from "../../api/client";
import {
  getAuthFeatures,
  getCurrentUser,
  listMySessions,
  revokeMySession,
} from "../../api/client";
import { CompGrid, type Column } from "../../components/grid/CompGrid";
import { Panel } from "../../components/layout/Panel";
import { useAuth } from "../../contexts/AuthContext";
import { ChangeEmailDialog, ChangePasswordDialog } from "./UserProfileDialogs";

export function UserProfileSettings() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const accessToken = auth.accessToken;
  const [me, setMe] = useState<User | null>(null);
  const [passwordChangeEnabled, setPasswordChangeEnabled] = useState(false);
  const [emailChangeEnabled, setEmailChangeEnabled] = useState(false);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canCallApi = auth.status === "authenticated" && !!accessToken;

  const refreshMe = useCallback(
    async (signal?: AbortSignal) => {
      if (!accessToken) return;
      const u = await getCurrentUser(accessToken, signal);
      setMe(u);
    },
    [accessToken],
  );

  const refreshSessions = useCallback(
    async (signal?: AbortSignal) => {
      if (!accessToken) return;
      const res = await listMySessions(accessToken, signal);
      setSessions(res);
    },
    [accessToken],
  );

  useEffect(() => {
    if (!canCallApi) return;
    const abort = new AbortController();
    setError(null);
    setSuccess(null);
    setLoading(true);
    refreshMe(abort.signal)
      .catch((e) => {
        if (abort.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load profile");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoading(false);
      });
    return () => abort.abort();
  }, [canCallApi, refreshMe]);

  useEffect(() => {
    getAuthFeatures()
      .then((f) => {
        setPasswordChangeEnabled(f.password_change_enabled);
        setEmailChangeEnabled(f.email_change_enabled ?? false);
      })
      .catch(() => {
        setPasswordChangeEnabled(false);
        setEmailChangeEnabled(false);
      });
  }, []);

  useEffect(() => {
    if (searchParams.get("email_change") === "success") {
      setSuccess("Email updated successfully.");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("email_change");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!canCallApi) return;
    const abort = new AbortController();
    setLoadingSessions(true);
    refreshSessions(abort.signal)
      .catch(() => {
        // Sessions endpoint might not be enabled yet; keep it non-fatal.
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoadingSessions(false);
      });
    return () => abort.abort();
  }, [canCallApi, refreshSessions]);

  const columns: Column<UserSession>[] = useMemo(
    () => [
      {
        id: "id",
        label: "Session",
        sortable: true,
        sortValue: (s) => s.token_id,
        render: (s) => (
          <Typography variant="body2" className="om-text-mono" noWrap>
            {s.token_id}
          </Typography>
        ),
      },
      {
        id: "ua",
        label: "User agent",
        render: (s) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
            {s.user_agent || "-"}
          </Typography>
        ),
      },
      {
        id: "ip",
        label: "IP",
        render: (s) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} className="om-text-mono" noWrap>
            {s.ip_address || "-"}
          </Typography>
        ),
      },
      {
        id: "expires",
        label: "Expires",
        sortable: true,
        sortValue: (s) => (s.expires_at ? new Date(s.expires_at) : new Date(0)),
        render: (s) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
            {s.expires_at ? new Date(s.expires_at).toLocaleString() : "-"}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 1 }}>Actions</Box>,
        render: (s) => (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="text"
              disabled={!canCallApi || loadingSessions}
              onClick={async () => {
                if (!accessToken) return;
                try {
                  setError(null);
                  setSuccess(null);
                  await revokeMySession(accessToken, s.token_id);
                  await refreshSessions();
                  setSuccess("Session revoked");
                } catch (e) {
                  setError(e instanceof Error ? e.message : "Failed to revoke session");
                }
              }}
            >
              Revoke
            </Button>
          </Box>
        ),
      },
    ],
    [accessToken, canCallApi, loadingSessions],
  );

  if (auth.status !== "authenticated") {
    return (
      <Panel title="User profile">
        <Alert severity="info">Please log in to view your profile.</Alert>
      </Panel>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel title="User profile">
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
              {loading ? "—" : me?.email ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Display name
            </Typography>
            <Typography variant="body1">
              {loading ? "—" : (me?.username?.trim() ? me.username : "—")}
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {emailChangeEnabled ? (
              <Button
                variant="outlined"
                size="small"
                disabled={!canCallApi || loading}
                onClick={() => setEmailDialogOpen(true)}
              >
                Change email
              </Button>
            ) : null}
            {passwordChangeEnabled ? (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={!canCallApi || loading}
                onClick={() => setPasswordDialogOpen(true)}
              >
                Change password
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {accessToken ? (
          <>
            <ChangeEmailDialog
              open={emailDialogOpen}
              onClose={() => setEmailDialogOpen(false)}
              currentEmail={me?.email ?? ""}
              accessToken={accessToken}
              onSuccess={(message) => {
                setSuccess(message);
                void refreshMe();
              }}
            />
            <ChangePasswordDialog
              open={passwordDialogOpen}
              onClose={() => setPasswordDialogOpen(false)}
              currentEmail={me?.email ?? ""}
              accessToken={accessToken}
              onSuccess={() => setSuccess("Password updated")}
            />
          </>
        ) : null}
      </Panel>

      <Panel title="Sessions" subtitle="Refresh-token sessions (if enabled on server)">
        <CompGrid
          items={sessions}
          loading={loadingSessions}
          error={null}
          emptyMessage="No active sessions found (or endpoint not enabled)."
          columns={columns}
          keyExtractor={(s) => s.token_id}
        />
      </Panel>
    </Box>
  );
}

